import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  Users,
  BookOpen,
  GraduationCap,
  Trophy,
  BookMarked,
  UserCheck,
  Briefcase,
  ChevronRight,
  ChevronLeft,
  Quote,
  ArrowRight,
  Calendar,
  Sparkles,
} from "lucide-react";
import {
  heroSlides,
  highlights,
  features,
  courses,
  testimonials,
  news,
} from "../mock/mock";

const iconMap = {
  Award,
  Users,
  BookOpen,
  GraduationCap,
  Trophy,
  BookMarked,
  UserCheck,
  Briefcase,
};

const Home = () => {
  const [slide, setSlide] = useState(0);
  const [tIdx, setTIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setInterval(() => setTIdx((s) => (s + 1) % testimonials.length), 7000);
    return () => clearInterval(t);
  }, []);

  const current = heroSlides[slide];
  const testimonial = testimonials[tIdx];

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-brand-green-deep">
        {heroSlides.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === slide ? "opacity-100" : "opacity-0"}`}
          >
            <img
              src={s.image}
              alt={s.title}
              className={`absolute inset-0 w-full h-full object-cover ${i === slide ? "ken-burns" : ""}`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-green-deep/85 via-brand-green-deep/60 to-brand-green-deep/20" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(212,175,55,0.15),transparent_60%)]" />
          </div>
        ))}

        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex items-center">
          <div className="max-w-2xl text-brand-ivory">
            <div key={slide} className="animate-fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-gold/40 bg-brand-gold/10 text-brand-gold text-xs uppercase tracking-[0.2em] mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                Est. 2008 &middot; NCTE Approved
              </div>
              <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6">
                {current.title.split(" ").slice(0, -2).join(" ")}{" "}
                <span className="text-brand-gold">{current.title.split(" ").slice(-2).join(" ")}</span>
              </h1>
              <p className="text-lg md:text-xl text-brand-ivory/85 max-w-xl mb-8 leading-relaxed">
                {current.subtitle}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/admissions"
                  className="btn-gold px-7 py-3.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                >
                  {current.cta} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/about"
                  className="px-7 py-3.5 rounded-full text-sm font-semibold border border-brand-ivory/40 text-brand-ivory hover:bg-brand-ivory hover:text-brand-green-deep transition-colors"
                >
                  Discover More
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* controls */}
        <div className="absolute bottom-8 left-0 right-0 z-10 max-w-7xl mx-auto px-6 flex items-end justify-between">
          <div className="flex gap-2">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`h-1 transition-all duration-500 ${
                  i === slide ? "w-12 bg-brand-gold" : "w-6 bg-brand-ivory/40 hover:bg-brand-ivory/70"
                }`}
                aria-label={`slide ${i + 1}`}
              />
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setSlide((slide - 1 + heroSlides.length) % heroSlides.length)}
              className="w-11 h-11 rounded-full border border-brand-ivory/30 text-brand-ivory hover:bg-brand-gold hover:text-brand-green-deep hover:border-brand-gold transition-colors flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setSlide((slide + 1) % heroSlides.length)}
              className="w-11 h-11 rounded-full border border-brand-ivory/30 text-brand-ivory hover:bg-brand-gold hover:text-brand-green-deep hover:border-brand-gold transition-colors flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS STRIP */}
      <section className="bg-brand-ivory border-y border-brand-gold/20">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {highlights.map((h, i) => {
            const Icon = iconMap[h.icon];
            return (
              <div key={i} className="flex items-center gap-4 animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="w-14 h-14 rounded-full bg-brand-green flex items-center justify-center border-2 border-brand-gold shrink-0">
                  <Icon className="w-6 h-6 text-brand-gold" />
                </div>
                <div>
                  <div className="font-serif-display text-3xl font-bold text-brand-green-deep">{h.stat}</div>
                  <div className="text-xs uppercase tracking-widest text-brand-green/70">{h.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="section-y bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-brand-gold rounded-sm hidden md:block" />
            <img
              src="https://images.unsplash.com/photo-1509062522246-3755977927d7"
              alt="About Us"
              className="relative rounded-sm w-full h-[520px] object-cover shadow-2xl"
            />
            <div className="absolute -bottom-8 -right-6 hidden md:block bg-brand-green-deep text-brand-ivory p-6 rounded-sm shadow-2xl max-w-xs">
              <div className="font-serif-display text-4xl font-bold text-brand-gold mb-1">16+</div>
              <div className="text-xs uppercase tracking-widest text-brand-ivory/70">Years of nurturing educators</div>
            </div>
          </div>
          <div>
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] font-medium mb-4">About the College</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-brand-green-deep leading-tight mb-6">
              Nurturing educators, <span className="text-brand-gold-dark italic">nurturing nations.</span>
            </h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              Successful and happy individuals build a successful nation. But what aids success is a strong foundation in
              good education. At SHES D.Ed College, we craft learning experiences that lay the groundwork for
              a lifelong journey in teaching.
            </p>
            <p className="text-neutral-700 leading-relaxed mb-8">
              With students driven by determination, faculty of the highest calibre, meticulously designed curriculum, and
              optimum infrastructure, we prepare educators who can transform classrooms &mdash; and lives.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {features.slice(0, 4).map((f, i) => {
                const Icon = iconMap[f.icon];
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-brand-ivory flex items-center justify-center border border-brand-gold/40 shrink-0">
                      <Icon className="w-5 h-5 text-brand-green" />
                    </div>
                    <div>
                      <div className="font-semibold text-brand-green-deep text-sm">{f.title}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Link to="/about" className="btn-primary px-7 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">
              Read Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* COURSES PREVIEW */}
      <section className="section-y bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] font-medium mb-4">Our Programs</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-brand-green-deep">
              Programs that shape <span className="text-brand-gold-dark italic">tomorrow.</span>
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-neutral-600">
              From foundational teacher training to specialized diplomas, our courses blend rigorous academics with real-world practice.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {courses.map((c, i) => (
              <div
                key={c.id}
                className="bg-white rounded-sm border border-brand-gold/25 p-8 hover-lift group animate-fade-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="text-xs tracking-[0.25em] uppercase text-brand-gold-dark font-semibold">{c.code}</span>
                  <span className="text-xs text-neutral-500">{c.duration}</span>
                </div>
                <h3 className="font-serif-display text-2xl text-brand-green-deep mb-3 leading-tight">{c.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed mb-6 min-h-[72px]">{c.desc}</p>
                <div className="flex items-center justify-between pt-5 border-t border-brand-gold/20">
                  <span className="text-xs text-neutral-500">{c.seats} Seats</span>
                  <Link
                    to="/courses"
                    className="text-sm text-brand-green font-semibold group-hover:text-brand-gold-dark transition-colors inline-flex items-center gap-1"
                  >
                    Explore <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="section-y bg-brand-green-deep text-brand-ivory relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.15),transparent_50%)]" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Quote className="w-14 h-14 text-brand-gold/40 mx-auto mb-6" />
          <div key={testimonial.id} className="animate-fade-up">
            <p className="font-serif-body text-2xl md:text-3xl leading-relaxed mb-10 text-brand-ivory">
              &ldquo;{testimonial.quote}&rdquo;
            </p>
            <div className="flex items-center justify-center gap-4">
              <img
                src={testimonial.avatar}
                alt={testimonial.name}
                className="w-14 h-14 rounded-full border-2 border-brand-gold"
              />
              <div className="text-left">
                <div className="font-semibold text-brand-gold">{testimonial.name}</div>
                <div className="text-xs uppercase tracking-widest text-brand-ivory/70">
                  {testimonial.batch} &middot; {testimonial.role}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-10">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setTIdx(i)}
                className={`h-2 rounded-full transition-all ${
                  i === tIdx ? "w-8 bg-brand-gold" : "w-2 bg-brand-ivory/30 hover:bg-brand-ivory/60"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section className="section-y bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <div className="divider-ornament text-xs uppercase tracking-[0.3em] font-medium mb-3">Latest Updates</div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-brand-green-deep">News &amp; Events</h2>
            </div>
            <Link to="/gallery" className="btn-outline px-5 py-2.5 rounded-full text-sm font-semibold">
              View Gallery
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {news.map((n, i) => (
              <article
                key={n.id}
                className="group bg-brand-ivory rounded-sm p-6 hover-lift border border-brand-gold/20 animate-fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="flex items-center gap-2 text-xs text-brand-green/70 mb-4">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(n.date).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
                <span className="inline-block text-[10px] uppercase tracking-widest text-brand-gold-dark font-bold mb-3">
                  {n.category}
                </span>
                <h3 className="font-serif-display text-xl text-brand-green-deep leading-snug mb-3 group-hover:text-brand-gold-dark transition-colors">
                  {n.title}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{n.excerpt}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-brand-green-deep rounded-sm p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.2),transparent_60%)]" />
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-2xl">
                <h3 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-ivory mb-3">
                  Ready to begin your <span className="text-brand-gold">teaching journey?</span>
                </h3>
                <p className="text-brand-ivory/80">Admissions for the 2025-27 batch are now open. Reserve your seat today.</p>
              </div>
              <Link
                to="/admissions"
                className="btn-gold px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 shrink-0"
              >
                Apply Now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
